import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import FuelAnnexRoutesValidator from "../validators/FuelAnnexRoutesValidator";
import AirportLookup from "../../Airports/components/AirportsLookup";

import { useFuelAnnexRoutes, useAddFuelAnnexRoute, useUpdateFuelAnnexRoute, useDeleteFuelAnnexRoute } from "../../../services/fuel";
import { 
  MaskedText, 
  MaskedNumber,
} from "../../../controls";
import { defaultComponents } from '../../../controls/Material-Table-Components';


const FuelAnnexRouteList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(FuelAnnexRoutesValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddFuelAnnexRoute = useAddFuelAnnexRoute();
  const handleUpdateFuelAnnexRoute = useUpdateFuelAnnexRoute();
  const handleDeleteFuelAnnexRoute = useDeleteFuelAnnexRoute();

  const queryClient = useQueryClient();
  const extraKeys = [props.fuelAnnexSectorUuid];

  const fuelAnnexRoutes = useFuelAnnexRoutes({tableRef, queryClient, extraKeys});

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        title: "Fuel Annex Sector Uuid",
        field: "fuelAnnexSectorUuid",
        emptyValue: null,
        initialEditValue: null,  
        hidden: props.fuelAnnexSectorUuid !== ""?true:false,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedText
            mask={"i".repeat(36)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "#",
        field: "sequence",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(2)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.sequence}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        title: "Origin Airport Code",
        field: "originAirportCode",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Destination Airport Code",
        field: "destinationAirportCode",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Uplift Amount",
        field: "upliftAmount",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={0}
            fixedDecimalScale={false}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.upliftAmount}
            decimalScale={0}
            fixedDecimalScale={false}
          />
        )
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{marginLeft: 40}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 5,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Route Legs"
            columns={getColumns({canEdit: state.canEdit})}
            data={fuelAnnexRoutes.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddFuelAnnexRoute.mutate({record: {fuelAnnexSectorUuid: props.fuelAnnexSectorUuid, ...newData}, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateFuelAnnexRoute.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteFuelAnnexRoute.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default FuelAnnexRouteList;
